import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { TextField, MessageBar, MessageBarType, PrimaryButton, Label } from 'office-ui-fabric-react';
import * as _yup from "yup";
import 'react-sortable-tree/style.css';
import moment from "moment"
import slugify from 'slugify';

import { RootState } from "app/redux/reducer";
import { setPageData, setPageErrors, initialPageData } from 'app/redux/page/actions';
import { toggleConfirmDialog } from 'app/redux/system/actions';
import { MetaTypeEnum, StatusEnum } from 'app/shared';
import { castThunkAction } from 'app/utils/casting';
import { toggleModal } from 'app/redux/modal/actions';
import { updateArtist, deleteArtist, getArtist, getArtistByLanguage, IArtist } from 'app/services/artist';

import PageTitle from 'components/customs/PageTitle';
import Sidebar from 'components/customs/Sidebar/Sidebar';
import PageLayout from 'containers/PageLayout';
import { getConfiguration, IThemeSettings } from 'app/services/configuration';
import TranslatorPostBar from 'components/customs/TranslatorBar/TranslatorPostBar';
import MetaSingleFile from 'components/customs/MetaFields/Fields/MetaSingleFile';
import FormAccordion from 'components/customs/FormAccordion';
import MetaEditor from 'components/customs/MetaFields/Fields/MetaEditor';

function mapStateToProps(state: RootState, ownProps: RouteComponentProps<{ artistId: string }>) {
    return {
        ...ownProps,
        defaultLanguage: state.system.defaultLanguage,
        artistId: ownProps.match.params.artistId,
        language: state.system.language,
        pageData: state.page.pageData as IArtist,
        pageErrors: state.page.pageErrors,
        contentTypes: state.contentType.contentTypes.list
    }
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        ...bindActionCreators({
            setPageData,
            setPageErrors,
            initialPageData,
            updateArtist,
            deleteArtist,
            toggleConfirmDialog,
            getArtist,
            getArtistByLanguage,
            getConfiguration,
            toggleModal
        }, dispatch)
    };
}

type EditArtistProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface OwnState {
    messageType: MessageBarType;
    configurationData: IThemeSettings;
}

class EditArtist extends React.Component<EditArtistProps, OwnState> {

    state = {
        messageType: MessageBarType.warning,
        configurationData: {} as IThemeSettings,
    }

    UNSAFE_componentWillMount() {
        castThunkAction<IThemeSettings>(this.props.getConfiguration(true)).then(configuration => {
            if (configuration) {
                this.setState({
                    configurationData: configuration
                })
            }
            this.props.getArtist(this.props.artistId);
        })
    }

    resetData() {
        this.setState({
            messageType: MessageBarType.warning
        }, () => {
            this.props.initialPageData<IArtist>({
                key: "",
                adminTitle: "",
                title: "",
                description: "",
                content: "",
                order: 30,
                language: this.props.defaultLanguage,
                relationId: "",
            })
        })
    }

    renderTextByMessageType() {
        switch (this.state.messageType) {
            case MessageBarType.success:
                return "Kaydetme işlemi başarıyla gerçekleşmiştir."
            case MessageBarType.error:
                return "Kayıt işlemi yapılırken bir hata oluşmuştur. Lütfen servis sağlayıcınızla iletişime geçin."
            default:
                return "Aşağıdaki değerler üzerinden düzenleme yapabilirsiniz."
        }
    }

    renderMessageBar() {
        if (this.state.messageType !== MessageBarType.warning)
            return (
                <MessageBar
                    messageBarType={this.state.messageType}
                    isMultiline={false}
                >
                    {this.renderTextByMessageType()}
                </MessageBar>
            )
        return null;
    }

    onChangeLanguage(language: string) {
        if (language !== this.props.pageData.language) {
            this.props.getArtistByLanguage(this.props.pageData, language);
        }
    }

    onCloneLanguage() {
        this.props.getArtistByLanguage(this.props.pageData, this.props.pageData.language, true)
    }

    renderTranslatorBar() {
        if (!this.state.configurationData?.config?.languages || this.state.configurationData?.config?.languages.length <= 1) return null;

        return (
            <TranslatorPostBar
                languages={this.state.configurationData?.config?.languages?.map(item => item.code?.split("-")[0])}
                onChange={(language) => this.onChangeLanguage(language)}
                toggleDialog={this.props.toggleConfirmDialog}
                current={this.props.pageData.language}
                onClone={() => this.onCloneLanguage()}
                default={this.props.defaultLanguage}
            />
        )
    }

    onDelete() {
        this.props.toggleConfirmDialog(true, {
            callback: () => {
                let data = this.props.pageData;

                if (this.props.pageData.language !== this.props.defaultLanguage) {
                    castThunkAction<IArtist>(this.props.getArtist(this.props.artistId as string, this.props.defaultLanguage, true)).then(response => {
                        data = response;
                        castThunkAction(this.props.deleteArtist(this.props.pageData.id as string)).then(res => {
                            if (res) {
                                this.props.initialPageData(data)
                            }
                        });
                    });
                }
                else {
                    castThunkAction(this.props.deleteArtist(this.props.pageData.id as string)).then(res => {
                        if (res) {
                            this.props.history.push("/artists")
                        }
                    })
                }

            }
        })
    }

    /**
     * Validation Şemasını Kontrol Ederek, Eğer Validate ise Doğrulama modalını aktive eder, Değilse İlgili Yerlere Hata Dönecek Yani,
     * formErrors Objesini Dolduracak Action'u Tetikler.
     */
    onSubmit() {
        const {
            pageData,
            setPageErrors
        } = this.props;
        const validationSchema = _yup.object({
            title: _yup.string().required(
                "Bu alan zorunludur!"
            )
        });
        let formErrors = {};
        validationSchema
            .validate(pageData, {
                abortEarly: false
            })
            .then(() => {
                setPageErrors({});
                castThunkAction(this.props.updateArtist(this.props.pageData)).then(res => {
                    window.scrollTo({ top: 0 });
                    this.setState({
                        messageType: res ? MessageBarType.success : MessageBarType.error
                    })
                })
            })
            .catch(err => {
                err.inner.forEach((e: any) => {
                    const { path, message } = e;
                    formErrors[path] = message;
                });
                setPageErrors(formErrors);
            });
    };


    renderForm() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12">
                    <FormAccordion showContent={true} title="Genel Bilgiler">
                        <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
                            <div className="item row col-md-4">
                                <TextField
                                    className="custom-textfield"
                                    label="Sanatçı Adı"
                                    value={this.props.pageData.title}
                                    onChange={(event: any) => {
                                        this.props.initialPageData({
                                            ...this.props.pageData,
                                            title: event.target.value,
                                            adminTitle: event.target.value,
                                            key: slugify(event.target.value, { lower: true, replacement: '-', strict: true })
                                        })
                                    }}
                                />
                            </div>
                            <div className="item row col-md-4">
                                <TextField
                                    className="custom-textfield"
                                    label="Sanatçı Admin Adı"
                                    value={this.props.pageData.adminTitle}
                                    onChange={(event: any) => {
                                        this.props.setPageData("adminTitle", event.target.value)
                                    }}
                                />
                            </div>
                            <div className="item row col-md-4">
                                <TextField
                                    className="custom-textfield"
                                    label="Sanatçı Anahtarı"
                                    value={this.props.pageData.key}
                                    onChange={(event: any) => {
                                        this.props.setPageData("key", event.target.value)
                                    }}
                                />
                            </div>
                            <div className="item row col-md-12">
                                <TextField
                                    className="custom-textfield"
                                    label="Sanatçı Açıklaması"
                                    value={this.props.pageData.description}
                                    onChange={(event: any) => {
                                        this.props.setPageData("description", event.target.value)
                                    }}
                                    multiline={true}
                                    resizable={false}
                                />
                            </div>
                            <div className="item row col-md-12">
                                <Label>Sanatçı İçeriği</Label>
                                <MetaEditor
                                    type={MetaTypeEnum.Editor}
                                    onChange={(value: string) => {
                                        this.props.setPageData("content", value)
                                    }}
                                    meta={{ data: this.props.pageData.content, type: MetaTypeEnum.Editor, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }

    renderImages() {
        return (
            <div className="form-groups row">
                <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
                    <FormAccordion showContent={true} title="Görsel Ayarları">
                        <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
                            <div className="item row col-md-6">
                                <Label>Yatay Görsel</Label>
                                <MetaSingleFile
                                    type={MetaTypeEnum.SingleImage}
                                    onChange={(value) => {
                                        this.props.setPageData("profileImageHorizontal", value)
                                    }}
                                    meta={{ data: this.props.pageData.profileImageHorizontal, type: MetaTypeEnum.SingleImage, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                            <div className="item row col-md-6">
                                <Label>Dikey Görsel</Label>
                                <MetaSingleFile
                                    type={MetaTypeEnum.SingleImage}
                                    onChange={(value) => {
                                        this.props.setPageData("profileImageVertical", value)
                                    }}
                                    meta={{ data: this.props.pageData.profileImageVertical, type: MetaTypeEnum.SingleImage, title: '', column: 12, name: '' }}
                                    toggleModal={toggleModal}
                                />
                            </div>
                        </div>
                    </FormAccordion>
                </div>
            </div>
        )
    }
    render() {
        const { pageData } = this.props;
        return (
            <PageLayout history={this.props.history} match={this.props.match} location={this.props.location}>
                <div className="page push-all">
                    <PageTitle
                        title="Düzenle"
                        titleRenderer={
                            () => (
                                <span className="edit-title">
                                    Düzenle:
                                    <strong>{this.props.pageData?.title || "-"}</strong>
                                </span>
                            )
                        }
                        showBack
                        backUrl="/artists"
                        history={this.props.history}
                        subTitle="Aşağıdan geçerli sanatçıyı düzenleyebilirsiniz."
                    />
                    <PrimaryButton
                        onClick={() => this.props.history.push('/artist/create')}
                        iconProps={{ iconName: "Add" }}
                        className="absolute-create"
                        text="Yeni Sanatçı Ekle"
                        styles={{
                            root: {
                                background: "#62a98e",
                                borderColor: "#5b9e85",
                                color: "#fff"
                            },
                            rootHovered: {
                                background: "#5b9e85",
                                borderColor: "#5b9e85"
                            },
                            rootDisabled: {
                                background: "#ececec"
                            }
                        }}
                    />
                    {this.renderTranslatorBar()}
                    <div className="content row">
                        <div className="left col no-padding">
                            {this.renderMessageBar()}
                            {this.renderForm()}
                            {this.renderImages()}
                        </div>
                        <Sidebar
                            publishBar={{
                                onSave: () => this.onSubmit(),
                                onDelete: () => this.onDelete(),
                                hidePreviewButton: true,
                                hideDraftButton: true,
                                isEdit: true,
                                status: StatusEnum.Published,
                                userLabel: pageData.updatedBy ? 'Ekleyen Kullanıcı:' : 'Düzenleyen Kullanıcı:',
                                publishDateText: pageData.updatedBy ? 'Yayınlanma Tarihi:' : 'Eklenme Tarihi:',
                                dateText: moment((pageData.updatedAt ? pageData.updatedAt : pageData.createdAt)).format('DD.MM.YYYY, HH:mm'),
                                showOrder: true,
                                order: this.props.pageData?.order || 0,
                                onChangeOrderIndex: (order) => this.props.setPageData("order", order),
                            }}
                            categoryBar={{
                                hide: true
                            }}
                            archiveBar={{
                                hide: true
                            }}

                            layoutBar={{
                                hide: true
                            }}
                            thumbnailBar={{
                                hide: true
                            }}
                        />
                    </div>
                </div>
            </PageLayout>
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(EditArtist);